import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calculator, Download, Info, TrendingUp, FileCheck, User, Share, Save, Share2, Pencil, Check, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PremiumDetails, VehicleDetails, PolicyDetails } from '../index';
import Button from '../../../../../../components/form-control/Button';
import moment from 'moment';
import { formatCurrencyAmount, getInstallmentDescriptionFromKey, getInstallmentLabelFromKey, showToast } from '../../../../../../utils/miscFunctions';
import { InstallmentPlan } from '../../../../../../resources/interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { getQuoteWidgetPayloadFromState, SET_SELECTED_INSTALLMENT, SET_SELECTED_INSTALLMENT_DATA } from '../../../../../../states/slices/quoteWidgetSlice';
import useUserAccount from '../../../../../../hooks/userAccountHook';
import useLayoutHook from '../../../../../../hooks/layoutHook';
import Tooltip from '../../../../../../components/ToolTip';
import FleetVehicleBreakdown from './FleetVehicleBreakdown';

interface PremiumDetailsStepProps {
  data?: PremiumDetails;
  vehicleData?: VehicleDetails;
  policyData?: PolicyDetails;
  onUpdate: (data: PremiumDetails) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFleet?: boolean;
}

const PremiumDetailsStep: React.FC<PremiumDetailsStepProps> = ({ data, vehicleData, policyData, onUpdate, onNext, onPrevious, isFleet }) => {
  const [selectedInstallmentKey, setSelectedInstallmentKey] = useState<string | undefined>(data?.selectedInstallment);
  const dispatch = useDispatch();
  const {user} = useUserAccount();
  const { screenWidth } = useLayoutHook();
  const dataFromQuoteCalculation = useSelector(getQuoteWidgetPayloadFromState);
  const quoteRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchParams] = useSearchParams();
  const isRenewal = searchParams.get('isRenewal') === 'true';
  // build-cache-buster: v2 — previous deploy served a stale chunk that
  // was missing this file's Edit Premium block despite the source being
  // correct on origin/test. Non-whitespace change forces webpack to
  // re-emit the chunk with a fresh content hash.

  // Renewal-only Edit Premium override. Renewals don't hit the Quote
  // step where an editable premium already exists, so we surface the
  // control here on the Premium Details step — the first place the
  // operator sees the carried-over amount. Writing the override to
  // wizardData.premium is sufficient: renewMotorPolicyDirect reads it
  // from renewalOverrides.premium at Confirm time and the backend
  // applies it to the draft's PaymentPlan.
  const [isEditingPremium, setIsEditingPremium] = useState(false);
  const [premiumInput, setPremiumInput] = useState('');
  const startEditPremium = (current: number | undefined) => {
    setPremiumInput(current != null ? String(current) : '');
    setIsEditingPremium(true);
  };
  const saveEditedPremium = () => {
    const value = Number(premiumInput);
    if (!Number.isFinite(value) || value <= 0) {
      showToast.error('Enter a valid premium amount greater than 0');
      return;
    }
    // The Total Premium display reads
    //   selectedInstallment?.totalPremium || data?.premiumAmount
    // where selectedInstallment resolves via
    //   getSelectedInstallment() => data?.installments?.[selectedInstallmentKey]
    // — NOT from wizardData.premium.selectedInstallmentData and NOT from
    // redux. So updating those two alone leaves the visible number stale
    // (the previous fix that only touched selectedInstallmentData + redux
    // silently missed this — the operator would hit Save and see the same
    // old number, and reasonably conclude "it didn't work"). Update the
    // installments map for the currently-selected key too so the display
    // picks up the override immediately.
    const currentKey = selectedInstallmentKey ?? data?.selectedInstallment;
    const currentInstallment = currentKey && data?.installments
      ? (data.installments as any)[currentKey]
      : undefined;
    const currentInitialDeposit = currentInstallment?.initialDeposit
      ?? data?.selectedInstallmentData?.initialDeposit;
    const currentTotalPremium = currentInstallment?.totalPremium
      ?? data?.selectedInstallmentData?.totalPremium;
    const nextInitialDeposit = (currentInitialDeposit != null
      && currentTotalPremium
      && currentInitialDeposit === currentTotalPremium)
      ? value
      : (currentInitialDeposit ?? value);
    const nextSelectedInstallmentData = {
      ...(data?.selectedInstallmentData ?? {}),
      ...(currentInstallment ?? {}),
      totalPremium: value,
      initialDeposit: nextInitialDeposit,
    };
    const nextInstallments = (data?.installments && currentKey)
      ? {
          ...data.installments,
          [currentKey]: {
            ...(currentInstallment ?? {}),
            totalPremium: value,
            initialDeposit: nextInitialDeposit,
          },
        }
      : data?.installments;
    onUpdate({
      ...(data ?? {}),
      premiumAmount: value,
      annualPremium: value,
      selectedInstallmentData: nextSelectedInstallmentData,
      installments: nextInstallments,
    } as PremiumDetails);
    // Redux mirror so the Payment step's Paystack amount (which reads
    // through the redux slice on the Payment page) reflects the override.
    dispatch(SET_SELECTED_INSTALLMENT_DATA(nextSelectedInstallmentData));
    setIsEditingPremium(false);
    showToast.success('Total premium updated');
  };

  const handleDownloadQuote = async () => {
    if (!quoteRef.current || isDownloading) return;
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(quoteRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const renderWidth = pageWidth - margin * 2;
      const renderHeight = (canvas.height * renderWidth) / canvas.width;

      if (renderHeight <= pageHeight - margin * 2) {
        pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);
      } else {
        const pageInnerHeight = pageHeight - margin * 2;
        const sliceHeightPx = (canvas.width * pageInnerHeight) / renderWidth;
        let yOffset = 0;
        let pageIndex = 0;
        while (yOffset < canvas.height) {
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(sliceHeightPx, canvas.height - yOffset);
          const ctx = sliceCanvas.getContext('2d');
          if (!ctx) break;
          ctx.drawImage(canvas, 0, yOffset, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height);
          const sliceImg = sliceCanvas.toDataURL('image/png');
          const sliceRenderHeight = (sliceCanvas.height * renderWidth) / canvas.width;
          if (pageIndex > 0) pdf.addPage();
          pdf.addImage(sliceImg, 'PNG', margin, margin, renderWidth, sliceRenderHeight);
          yOffset += sliceCanvas.height;
          pageIndex += 1;
        }
      }

      const reference = data?.quoteReference || moment().format('YYYYMMDD-HHmmss');
      pdf.save(`Brolly-Quote-${reference}.pdf`);
    } catch (err) {
      showToast.error('Failed to download quote. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };
  const handleInstallmentChange = (installmentKey: string) => {
    setSelectedInstallmentKey(installmentKey);
    dispatch(SET_SELECTED_INSTALLMENT(installmentKey));
    if (data) {
      const selectedInstallmentData = data.installments?.[installmentKey];
      dispatch(SET_SELECTED_INSTALLMENT_DATA(selectedInstallmentData));
      onUpdate({
        ...data,
        selectedInstallment: installmentKey,
        selectedInstallmentData: selectedInstallmentData
      } as any);
    }
  };

  React.useEffect(() => {
    if(data?.installments && Object.entries(data.installments).length === 1) {
      const onlyInstallmentKey = Object.keys(data.installments)[0];
      handleInstallmentChange(onlyInstallmentKey);
    }
  }, [])
  
  const convertedInstallmentsFromPremium = data?.installments &&
    Object.entries(data?.installments)
      .map(([key, installment]) => ({
        ...installment,
        key
      }));

  const orderedInstallments = (): InstallmentPlan[] => {
    const hasDailyInstallmnent = convertedInstallmentsFromPremium?.filter(i => i.key.includes('DAILY'));
    const containsDailyInstallment = hasDailyInstallmnent && hasDailyInstallmnent.length > 0;

    const list: InstallmentPlan[] = convertedInstallmentsFromPremium ?
      containsDailyInstallment ?
        [
          convertedInstallmentsFromPremium[2],
          convertedInstallmentsFromPremium[1],
          convertedInstallmentsFromPremium[0],
        ] :
        convertedInstallmentsFromPremium.length === 1
          ?
          [
            convertedInstallmentsFromPremium[0]
          ]
          :
          [
            convertedInstallmentsFromPremium[4],
            convertedInstallmentsFromPremium[1],
            convertedInstallmentsFromPremium[2],
            convertedInstallmentsFromPremium[3],
            convertedInstallmentsFromPremium[0]
          ]
      : [];
    return list;
  }

  const getSelectedInstallment = () => {
    if (selectedInstallmentKey) {
      return data?.installments?.[selectedInstallmentKey];
    }
    return null;
  };

  const selectedInstallment = getSelectedInstallment();
  const adjustedTotalPremium = selectedInstallment?.totalPremium;

  const handleNext = () => {
    if (!selectedInstallment) {
      showToast.error('Please select a payment option');
      return;
    }
    localStorage.removeItem(`policyWizardStep`)
    localStorage.removeItem(`policyWizardData`)
    onNext();
  }

  return (
    <div className="bg-white dark:bg-darkBg dark:border dark:border-darkBg-border rounded-lg shadow-sm p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText flex items-center">
            <FileCheck className="h-6 w-6 mr-2 text-primary-main" />
            Premium Details
          </h2>
          <p className="text-gray-600 mt-1 dark:text-darkBg-subText">Review the premium calculation and payment options</p>
        </div>
        {dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR && (
          <button
            type="button"
            onClick={handleDownloadQuote}
            disabled={isDownloading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary-main text-gray-900 hover:bg-opacity-90 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? 'Preparing…' : 'Download Quote'}
          </button>
        )}
      </div>

      <div ref={quoteRef} className="space-y-6">
        {/* Premium Breakdown */}
        <div className="bg-primary-main/10 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4 flex items-center">
            <Calculator className="h-5 w-5 mr-2" />
            Premium Breakdown
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-darkBg-subText">Base Premium</span>
                <span className="font-medium dark:text-darkBg-mainText"> {formatCurrencyAmount(data?.basePremium)}</span>
              </div>

              {(data?.additionalPremium || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-darkBg-subText">Additional Premium</span>
                  <span className="font-medium dark:text-darkBg-mainText">+{formatCurrencyAmount(data?.additionalPremium)}</span>
                </div>
              )}

              {(data?.excessPremium || 0) > 0 && (
                <div className="flex justify-between items-center text-green-600">
                  <span>Excess Premium</span>
                  <span className="font-medium">-{formatCurrencyAmount(data?.excessPremium)}</span>
                </div>
              )}

              {(data?.extraTppdlPremium || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-darkBg-subText">Extra TPPDL</span>
                  <span className="font-medium dark:text-darkBg-mainText">+{formatCurrencyAmount(data?.extraTppdlPremium)}</span>
                </div>
              )}

              {(data?.umbrellaPremium || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-darkBg-subText">Umbrella</span>
                  <span className="font-medium dark:text-darkBg-mainText">+{formatCurrencyAmount(data?.umbrellaPremium)}</span>
                </div>
              )}

              {(data?.noClaimDiscount || 0) > 0 && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="text-gray-600 dark:text-darkBg-subText">No Claim Discount ({data?.noClaimDiscountPercentage || 0}%)</span>
                  <span className="font-medium dark:text-darkBg-mainText">-{formatCurrencyAmount(data?.noClaimDiscount)}</span>
                </div>
              )}

              {(isFleet || dataFromQuoteCalculation?.IS_FLEET_QUOTE_CALCULATOR) && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="text-gray-600 dark:text-darkBg-subText">Fleet Discount ({data?.fleetDiscountPercentage || 0}%)</span>
                  <span className="font-medium dark:text-darkBg-mainText">-{formatCurrencyAmount(data?.fleetDiscountAmount)}</span>
                </div>
              )}

              <hr className="border-gray-300" />

              <div className="flex justify-between items-center text-lg font-bold gap-2">
                <span className=" dark:text-darkBg-subText">Total Premium</span>
                {isRenewal && isEditingPremium ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-normal text-gray-600 dark:text-darkBg-subText">{data?.currencyCode?.key || 'GHS'}</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      autoFocus
                      value={premiumInput}
                      onChange={(e) => setPremiumInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEditedPremium();
                        if (e.key === 'Escape') setIsEditingPremium(false);
                      }}
                      className="w-32 border border-gray-300 rounded px-2 py-1 text-right text-base font-semibold focus:outline-none focus:ring-1 focus:ring-primary-main dark:bg-darkBg dark:border-darkBg-border dark:text-darkBg-mainText"
                    />
                    <button
                      type="button"
                      aria-label="Save total premium"
                      onClick={saveEditedPremium}
                      className="p-1 rounded-full text-green-600 hover:bg-green-50 focus:outline-none">
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Cancel edit"
                      onClick={() => setIsEditingPremium(false)}
                      className="p-1 rounded-full text-gray-400 hover:bg-gray-100 focus:outline-none">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <span className="flex items-center gap-2 text-black dark:text-darkBg-mainText">
                    {data?.currencyCode?.key} {formatCurrencyAmount(selectedInstallment?.totalPremium || data?.premiumAmount)}
                    {isRenewal && (
                      <button
                        type="button"
                        aria-label="Edit total premium"
                        onClick={() => startEditPremium(selectedInstallment?.totalPremium ?? data?.premiumAmount)}
                        className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-primary-main focus:outline-none">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Quote Details */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-darkBg-mainText">Quote Details</h4>

              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-darkBg-subText">Quote Reference</span>
                  <span className="font-mono text-xs dark:text-darkBg-mainText">{data?.quoteReference || '-'}</span>
                </div>

                {!(isFleet || dataFromQuoteCalculation?.IS_FLEET_QUOTE_CALCULATOR) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-darkBg-subText">Cover Type</span>
                    <span className="dark:text-darkBg-mainText">{data?.insurerPortfolio?.name || '-'}</span>
                  </div>
                )}

                {!(isFleet || dataFromQuoteCalculation?.IS_FLEET_QUOTE_CALCULATOR) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-darkBg-subText">Risk Type</span>
                    <span className="dark:text-darkBg-mainText">{data?.riskType?.name || '-'}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-darkBg-subText">Policy Duration</span>
                  <span className="dark:text-darkBg-mainText">{data?.policyDurationDays ? Number(data?.policyDurationDays + 1) : '-'} days</span>
                </div>
              </div>
            </div>
          </div>

          {/* Per-vehicle breakdown — same component used on the Policy
              Review step so both surfaces render identical rows. Reads
              the priced vehicleQuotes from either the wizard's own
              PremiumDetails (`data`) or the quote-calculator entry
              (redux-fed dataFromQuoteCalculation.QUOTE_RESPONSE), then
              falls back to synthesised rows from the redux vehicles
              slice when neither is populated yet. */}
          <FleetVehicleBreakdown
            enabled={Boolean(isFleet || dataFromQuoteCalculation?.IS_FLEET_QUOTE_CALCULATOR)}
            vehicleQuotes={(data as any)?.vehicleQuotes
              ?? (dataFromQuoteCalculation?.QUOTE_RESPONSE as any)?.vehicleQuotes}
            fallbackVehicles={(dataFromQuoteCalculation as any)?.vehicles}
          />
        </div>

        {/* Quote Information */}
        <div className="bg-blue-50 dark:bg-darkBg dark:border dark:border-darkbg-border rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-darkBg-mainText mb-3">Quote Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Calculated At:</span>
              <span className="dark:text-darkBg-mainText">{data?.calculatedAt ? moment(data.calculatedAt).format('DD-MM-YYYY HH:mm:ss a') : '-'}</span>
            </div>
          </div>
        </div>

        {/* Payment Frequency Options */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Payment {data?.installments && Object.entries(data.installments).length > 1 ? 'Options' : 'Option'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data?.installments && orderedInstallments() ? orderedInstallments().map((installment: InstallmentPlan) => (
              <div
                key={installment?.key}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedInstallmentKey === installment?.key
                  ? 'border-primary-main bg-primary-main/10'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
                onClick={() => handleInstallmentChange(installment?.key)}
              >
                <div className="flex items-center mb-2">
                  <input
                    type="radio"
                    name="installmentOption"
                    value={installment?.key}
                    checked={selectedInstallmentKey === installment?.key || (Object.entries(data.installments)?.length === 1)}
                    onChange={() => handleInstallmentChange(installment?.key)}
                    className="text-primary-main focus:ring-primary-main"
                  />
                  <label className="ml-2 font-medium text-gray-900 dark:text-darkBg-mainText">{getInstallmentLabelFromKey(installment?.key)}</label>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{getInstallmentDescriptionFromKey(installment?.key, installment)}</p>

                <div className="text-lg font-bold text-black dark:text-darkBg-mainText mb-1">
                  {formatCurrencyAmount(installment?.totalPremium)}
                </div>

                <div className={`text-xs px-2 py-1 rounded-full inline-block ${installment?.key === 'FULL_PAYMENT'
                  ? 'bg-primary-main text-white'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                  {installment?.key === 'FULL_PAYMENT' ? 'Best Value' : getInstallmentLabelFromKey(installment?.key)}
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                <p>No installment options available</p>
              </div>
            )}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bg-primary-main/10 border border-primary-main rounded-lg p-4 dark:border-primary-main/50">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-primary-main mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium mb-2 dark:text-darkBg-mainText">Payment Summary</h4>
              <div className="text-sm space-y-1 dark:text-darkBg-mainText">
                <p>
                  <strong>Selected Plan:</strong> {selectedInstallmentKey ? getInstallmentLabelFromKey(selectedInstallmentKey) : '-'}
                </p>
                <p>
                  <strong>Total Premium:</strong> {formatCurrencyAmount(adjustedTotalPremium)}
                </p>
                {selectedInstallment && (
                  <>
                    <p><strong>Initial Deposit:</strong> {formatCurrencyAmount(selectedInstallment.initialDeposit)}</p>

                    {selectedInstallment?.monthlyInstallment > 0 && (
                      <p><strong>{`${selectedInstallmentKey === 'DAILY' ? 'Daily' : selectedInstallmentKey === 'WEEKLY' ? 'Weekly' : 'Monthly'}`} Payment:</strong> {formatCurrencyAmount(selectedInstallment.monthlyInstallment)}</p>
                    )}

                    <p><strong>Payment Period: </strong>
                      {selectedInstallment?.period === 1 ? ' One Time' : `${selectedInstallment?.period} ${selectedInstallmentKey === 'DAILY' ? 'Days' : selectedInstallmentKey === 'WEEKLY' ? 'Weeks' : 'Months'}`}

                    </p>
                  </>
                )}
                <p><strong>Policy Duration:</strong> {data?.policyDurationDays ? Number(data?.policyDurationDays + 1) : 0} days</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button
          onClick={onPrevious}
          icon={<ArrowLeft className="h-5 w-12 md:h-4 md:w-4" />}
          label={screenWidth <= 550 ? "" : "Previous"}
          className={screenWidth <= 550 ? '!p-0 !bg-transparent !border-none !shadow-none' : ''}
          bgColor='secondary'
        />

        {user.isBroker() && <div className='flex items-center gap-2'>
          {/* <Share2 className="h-5 w-5 text-primary-main"/>
          <Save className="h-5 w-5 text-primary-main"/> */}
          {!selectedInstallment ? <Tooltip message="Select a payment option to proceed" placement='top'>
            <div>
            <Button
              onClick={()=>{}}
              appendIcon={<ArrowRight className="h-4 w-4" />}
              // disabled={!selectedInstallment}
              bgColor="secondary"
              label={dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR ? "Create Policy" : "Next: Policy Details"} 
            />
            </div>
          </Tooltip>
          :  <Button
              onClick={handleNext}
              appendIcon={<ArrowRight className="h-4 w-4" />}
              disabled={!selectedInstallment}
              bgColor="primary"
              label={dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR ? "Create Policy" : "Next: Policy Details"} 
            />}
        </div>
        }
      </div>
    </div>
  );
};

export default PremiumDetailsStep;
